import { Flex, message } from 'antd';

import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { isValidShareRecipientContact } from 'utilities/share-recipient-validation';
import { shareDocuments } from 'slices/documentsActions';
import { ActionSharingCancelSave } from './ActionSharingCancelSave';
import { useApi } from '../../api/useApi';
import { IDevelopmentState } from '../../slices/developmentSlice';
import { setterShareable } from '../../slices/shareablesSlice';
import { setterInvitations } from '../../slices/invitationsSlice';

export const ActionSharingByLink = ({
    close,
    onSubmit,
    entity,
}: {
    close: () => void;
    onSubmit: () => void;
    entity: any;
}) => {
    const dispatch = useAppDispatch();
    const api = useApi();
    const { isDevelopmentMode } = useAppSelector<IDevelopmentState>((state) => state.development);
    const isNewSharingMode = isDevelopmentMode;

    const { shareRecipients, includeAnnotations } = useAppSelector((state) => state.shareables);
    const entityId = entity?.type === 'category' ? entity?.uuid : entity?.id;

    const getShareLinkErrorMessage = (error: unknown) => {
        if (error instanceof Error && error.message) {
            return error.message;
        }

        return 'Unable to share this resource. Please try again.';
    };

    const handleSave = async () => {
        const trimmedRecipients = shareRecipients.map((recipient) => ({
            ...recipient,
            contact: recipient.contact?.trim() ?? '',
        }));
        const filledRecipients = trimmedRecipients.filter((recipient) => recipient.contact);
        const validRecipients = filledRecipients.filter((recipient) =>
            isValidShareRecipientContact(
                recipient.contact ?? '',
                isNewSharingMode ? (recipient.contactType ?? 'email') : undefined,
            ),
        );
        const invalidRecipients = filledRecipients.filter(
            (recipient) =>
                !isValidShareRecipientContact(
                    recipient.contact ?? '',
                    isNewSharingMode ? (recipient.contactType ?? 'email') : undefined,
                ),
        );

        if (!filledRecipients.length) {
            dispatch(setterShareable({ shareRecipientsError: 'Enter at least one email or phone number' }));
            return;
        }

        if (invalidRecipients.length) {
            dispatch(setterShareable({ shareRecipientsError: 'Fix invalid email or phone number' }));
            return;
        }

        dispatch(setterShareable({ shareRecipientsError: '' }));

        let data = {
            entities: [entityId],
            recipients: validRecipients.map((recipient) => ({ contact: recipient.contact })),
            includeAnnotations: includeAnnotations,
        };

        if (isDevelopmentMode) {
            dispatch(setterInvitations({ loading: true }));
            try {
                await api.documents.shareLink(data);
                onSubmit();
                close();
            } catch (error) {
                const errorMessage = getShareLinkErrorMessage(error);
                message.error(errorMessage);
                dispatch(setterShareable({ shareRecipientsError: errorMessage }));
                console.error(error);
            } finally {
                dispatch(setterInvitations({ loading: false }));
            }
        } else {
            dispatch(shareDocuments(data))
                .unwrap()
                .then(() => {
                    onSubmit();
                    close();
                });
        }
    };

    return (
        <Flex style={{ width: '100%' }}>
            <ActionSharingCancelSave close={close} handleSave={handleSave} />
        </Flex>
    );
};
